<template>
  <v-container class="feed-page">
    <v-row justify="center">
      <v-col cols="12" lg="11" xl="9">
        <v-row>
          <!-- メインカラム (タイムライン) -->
          <v-col cols="12" md="8">
            <h1 class="feed-page-title">
              {{ $tt('feed.title') }}
              <v-chip x-small outlined color="primary" class="ml-1">BETA</v-chip>
              <v-btn icon small class="ml-2" :loading="loading" @click="load()">
                <v-icon small>mdi-refresh</v-icon>
              </v-btn>
              <v-btn icon small :title="$tt('feed.eventSettingsTitle')" @click="openFeedSettings()">
                <v-icon small>mdi-cog-outline</v-icon>
              </v-btn>
            </h1>
            <p class="feed-page-desc grey--text">{{ $tt('feed.description') }}</p>

            <v-alert v-if="error" type="warning" outlined>{{ $tt('feed.loadError') }}</v-alert>
            <v-progress-linear v-if="loading" indeterminate class="mb-4" />

            <template v-if="!loading && !error">
              <v-alert v-if="!cell" type="info" outlined dense class="mb-4">
                {{ $tt('feed.noAreaHint') }}
              </v-alert>

              <!-- 無料会員向け: 有料の窓・広域の案内 -->
              <v-alert v-if="!isPaidMember" type="info" outlined class="mb-4">
                {{ $tt('feed.upsell') }}
                <div class="mt-3">
                  <v-btn color="primary" to="/billing">{{ $tt('customBillingMenu') }}</v-btn>
                </div>
              </v-alert>

              <template v-for="item in timelineItems">
                <!-- 位置情報・形状つきイベント: 走行軌跡の投稿と同じリッチカード -->
                <v-card
                  v-if="item.type === 'post' && item.p.kind === 'event' && hasEventGeo(item.p)"
                  :key="'eg-' + item.p.id"
                  class="feed-card mb-4"
                >
                  <div class="feed-card-head">
                    <v-avatar size="42" color="blue-grey">
                      <v-icon dark>{{ eventIcon(item.p.event_type) }}</v-icon>
                    </v-avatar>
                    <div class="ml-3">
                      <div class="feed-card-title">{{ item.p.author_nickname || item.p.author_username }}</div>
                      <div class="caption grey--text">
                        {{ item.p.posted_date }} {{ postTime(item.p) }} ・ {{ visibilityLabel(item.p.visibility) }}
                        <template v-if="item.p.region && item.p.region.admin">
                          ・ {{ countryFlag(item.p.region.country) }} {{ item.p.region.admin }}
                        </template>
                      </div>
                    </div>
                    <v-spacer />
                    <v-btn v-if="item.p.is_mine" icon small @click="openVisibilityEdit(item.p)">
                      <v-icon small>mdi-lock-open-outline</v-icon>
                    </v-btn>
                    <v-btn v-if="item.p.is_mine" icon small @click="deleteTarget = item.p">
                      <v-icon small>mdi-delete-outline</v-icon>
                    </v-btn>
                  </div>
                  <v-card-text class="feed-card-body pt-1 pb-2">
                    <!-- タイトルは説明文に含まれるため省略し、画像の縦サイズを確保する -->
                    <div class="feed-event-desc grey--text">
                      <strong>{{ item.p.author_nickname || item.p.author_username }}</strong>
                      {{ eventLabel(item.p) }}
                    </div>
                    <!-- 形状: 絶対位置ありは地図背景、なしは形のみ (走行軌跡と同じ方針) -->
                    <div v-if="item.p.shape" class="feed-route mt-2">
                      <!-- VRA/高低差: ゾーンの濃淡・色分けを描画 -->
                      <svg
                        v-if="item.p.shape.type === 'zones'"
                        viewBox="0 0 100 56"
                        preserveAspectRatio="xMidYMid meet"
                      >
                        <path
                          v-for="(zp, zi) in zonePaths(item.p)"
                          :key="'zp-' + zi"
                          :d="zp.d"
                          :fill="zp.color"
                          fill-rule="evenodd"
                          stroke="#ffffff"
                          stroke-width="0.3"
                        />
                        <path
                          v-if="zoneOutlinePath(item.p)"
                          :d="zoneOutlinePath(item.p)"
                          fill="none"
                          stroke="#43A047"
                          stroke-width="0.8"
                          stroke-linejoin="round"
                        />
                      </svg>
                      <img
                        v-else-if="item.p.location"
                        :src="shapeMapUrl(item.p)"
                        class="feed-route-map"
                        loading="lazy"
                      />
                      <svg v-else viewBox="0 0 100 56" preserveAspectRatio="xMidYMid meet">
                        <path
                          :d="shapePathLarge(item.p)"
                          fill="rgba(67, 160, 71, 0.25)"
                          fill-rule="evenodd"
                          stroke="#43A047"
                          stroke-width="1"
                          stroke-linejoin="round"
                        />
                      </svg>
                    </div>
                    <div v-else-if="item.p.location" class="feed-route mt-2">
                      <img :src="pointMapUrl(item.p.location)" class="feed-route-map" loading="lazy" />
                    </div>
                    <div class="mt-2 d-flex align-center">
                      <v-btn text small :color="item.p.kudos_mine ? 'pink' : undefined" @click="toggleKudos(item.p)">
                        <v-icon small left>{{ item.p.kudos_mine ? 'mdi-heart' : 'mdi-heart-outline' }}</v-icon>
                        {{ item.p.kudos_count }}
                      </v-btn>
                      <v-menu v-if="item.p.is_mine" offset-y>
                        <template v-slot:activator="{ on }">
                          <v-btn text small v-on="on">
                            <v-icon small left>mdi-share-variant</v-icon>
                            {{ $tt('feed.snsShare') }}
                          </v-btn>
                        </template>
                        <v-list dense>
                          <v-list-item @click="shareToX(item.p)">
                            <v-list-item-title>X (Twitter)</v-list-item-title>
                          </v-list-item>
                          <v-list-item @click="shareToFacebook(item.p)">
                            <v-list-item-title>Facebook</v-list-item-title>
                          </v-list-item>
                          <v-list-item @click="copyShareLink(item.p)">
                            <v-list-item-title>{{ $tt('feed.copyLink') }}</v-list-item-title>
                          </v-list-item>
                        </v-list>
                      </v-menu>
                    </div>
                  </v-card-text>
                </v-card>
                <!-- 位置なしの操作イベントはコンパクトな1行カード -->
                <v-card
                  v-else-if="item.type === 'post' && item.p.kind === 'event'"
                  :key="'e-' + item.p.id"
                  class="feed-event-card mb-2"
                  outlined
                >
                  <v-card-text class="py-2 d-flex align-center">
                    <v-icon small class="mr-2" color="primary">{{ eventIcon(item.p.event_type) }}</v-icon>
                    <span>
                      <strong>{{ item.p.author_nickname || item.p.author_username }}</strong>
                      {{ eventLabel(item.p) }}
                      <span class="caption grey--text ml-2">
                        {{ item.p.posted_date }} {{ postTime(item.p) }} ・ {{ visibilityLabel(item.p.visibility) }}
                        <template v-if="item.p.region && item.p.region.admin"
                          >・ {{ item.p.region.admin }}</template
                        >
                      </span>
                    </span>
                    <!-- 圃場・マップの形状 (地図背景なしの形のみ) -->
                    <svg
                      v-if="shapeRings(item.p).length"
                      viewBox="0 0 60 40"
                      class="feed-event-shape ml-2"
                      preserveAspectRatio="xMidYMid meet"
                    >
                      <path
                        :d="shapePathSmall(item.p)"
                        fill="rgba(67, 160, 71, 0.25)"
                        fill-rule="evenodd"
                        stroke="#43A047"
                        stroke-width="1.5"
                        stroke-linejoin="round"
                      />
                    </svg>
                    <v-spacer />
                    <v-menu v-if="item.p.is_mine" offset-y>
                      <template v-slot:activator="{ on }">
                        <v-btn icon x-small v-on="on">
                          <v-icon x-small>mdi-share-variant</v-icon>
                        </v-btn>
                      </template>
                      <v-list dense>
                        <v-list-item @click="shareToX(item.p)">
                          <v-list-item-title>X (Twitter)</v-list-item-title>
                        </v-list-item>
                        <v-list-item @click="shareToFacebook(item.p)">
                          <v-list-item-title>Facebook</v-list-item-title>
                        </v-list-item>
                        <v-list-item @click="copyShareLink(item.p)">
                          <v-list-item-title>{{ $tt('feed.copyLink') }}</v-list-item-title>
                        </v-list-item>
                      </v-list>
                    </v-menu>
                    <v-btn v-if="item.p.is_mine" icon x-small @click="openVisibilityEdit(item.p)">
                      <v-icon x-small>mdi-lock-open-outline</v-icon>
                    </v-btn>
                    <v-btn v-if="item.p.is_mine" icon x-small @click="deleteTarget = item.p">
                      <v-icon x-small>mdi-delete-outline</v-icon>
                    </v-btn>
                  </v-card-text>
                </v-card>
                <!-- ユーザー投稿 (作業の共有) -->
                <v-card v-else-if="item.type === 'post'" :key="'p-' + item.p.id" class="feed-card mb-4">
                  <div class="feed-card-head">
                    <v-avatar size="42" color="blue-grey">
                      <v-icon dark>mdi-account</v-icon>
                    </v-avatar>
                    <div class="ml-3">
                      <div class="feed-card-title">{{ item.p.author_nickname || item.p.author_username }}</div>
                      <div class="caption grey--text">
                        {{ item.p.posted_date }} {{ postTime(item.p) }} ・ {{ visibilityLabel(item.p.visibility) }}
                        <template v-if="item.p.region && item.p.region.admin">
                          ・ {{ countryFlag(item.p.region.country) }} {{ item.p.region.admin }}
                        </template>
                      </div>
                    </div>
                    <v-spacer />
                    <template v-if="!item.p.is_mine">
                      <v-btn
                        v-if="!followState[item.p.author_username]"
                        text
                        x-small
                        color="primary"
                        @click="requestFollow(item.p)"
                      >
                        <v-icon x-small left>mdi-account-plus</v-icon>
                        {{ $tt('customFollow') }}
                      </v-btn>
                      <span v-else-if="followState[item.p.author_username] === 'pending'" class="caption grey--text">
                        {{ $tt('feed.followRequested') }}
                      </span>
                    </template>
                    <v-btn v-if="item.p.is_mine" icon small @click="openVisibilityEdit(item.p)">
                      <v-icon small>mdi-lock-open-outline</v-icon>
                    </v-btn>
                    <v-btn v-if="item.p.is_mine" icon small @click="deleteTarget = item.p">
                      <v-icon small>mdi-delete-outline</v-icon>
                    </v-btn>
                  </div>
                  <v-card-text class="feed-card-body">
                    <div class="feed-activity-title">
                      {{ item.p.operation_type || (item.p.text || '').split('\n')[0] }}
                    </div>
                    <div v-if="item.p.operation_type">{{ item.p.text }}</div>
                    <!-- 統計 (Strava 風: 地図の上に大きめの数字) -->
                    <div v-if="postStats(item.p).length" class="feed-post-stats mt-2">
                      <div v-for="st in postStats(item.p)" :key="st.label" class="feed-post-stat">
                        <div class="feed-stat-label">{{ st.label }}</div>
                        <div class="feed-post-stat-value">{{ st.value }}</div>
                      </div>
                    </div>
                    <!-- 作業経路: 既定は地図背景なしの形状のみ。正確位置の公開を選んだ投稿のみ地図背景 -->
                    <div v-if="item.p.route && item.p.route.length > 1" class="feed-route mt-2">
                      <img
                        v-if="item.p.location_exact"
                        :src="routeMapUrl(item.p.route)"
                        class="feed-route-map"
                        loading="lazy"
                      />
                      <svg v-else viewBox="0 0 100 56" preserveAspectRatio="xMidYMid meet">
                        <polyline
                          :points="routePoints(item.p.route)"
                          fill="none"
                          stroke="#43A047"
                          stroke-width="2"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                      </svg>
                    </div>
                    <!-- 写真 -->
                    <div v-if="item.p.photos && item.p.photos.length" class="feed-photos mt-2">
                      <img
                        v-for="ph in item.p.photos"
                        :key="ph.id"
                        :src="ph.thumb_url"
                        class="feed-photo-thumb"
                        loading="lazy"
                        @click="photoView = ph"
                      />
                    </div>
                    <div class="mt-2 d-flex align-center">
                      <v-btn text small :color="item.p.kudos_mine ? 'pink' : undefined" @click="toggleKudos(item.p)">
                        <v-icon small left>{{ item.p.kudos_mine ? 'mdi-heart' : 'mdi-heart-outline' }}</v-icon>
                        {{ item.p.kudos_count }}
                      </v-btn>
                      <!-- SNS シェア (全体公開の投稿のみ: シェアページが公開されるため) -->
                      <v-menu v-if="item.p.is_mine" offset-y>
                        <template v-slot:activator="{ on }">
                          <v-btn text small v-on="on">
                            <v-icon small left>mdi-share-variant</v-icon>
                            {{ $tt('feed.snsShare') }}
                          </v-btn>
                        </template>
                        <v-list dense>
                          <v-list-item @click="shareToX(item.p)">
                            <v-list-item-title>X (Twitter)</v-list-item-title>
                          </v-list-item>
                          <v-list-item @click="shareToFacebook(item.p)">
                            <v-list-item-title>Facebook</v-list-item-title>
                          </v-list-item>
                          <v-list-item @click="shareToInstagram(item.p)">
                            <v-list-item-title>Instagram</v-list-item-title>
                          </v-list-item>
                          <v-list-item @click="copyShareLink(item.p)">
                            <v-list-item-title>{{ $tt('feed.copyLink') }}</v-list-item-title>
                          </v-list-item>
                          <v-list-item v-if="canWebShare" @click="webShare(item.p)">
                            <v-list-item-title>{{ $tt('feed.shareOther') }}</v-list-item-title>
                          </v-list-item>
                        </v-list>
                      </v-menu>
                      <v-spacer />
                      <v-btn
                        v-if="!item.p.is_mine"
                        icon
                        small
                        :title="$tt('feed.report')"
                        @click="reportTarget = item.p"
                      >
                        <v-icon small color="grey">mdi-flag-outline</v-icon>
                      </v-btn>
                    </div>
                  </v-card-text>
                </v-card>

                <!-- ニュース/PR/シグナル投稿 -->
                <v-card v-else-if="item.type === 'announcement'" :key="'a-' + item.a.id" class="feed-card mb-4">
                  <div class="feed-card-head">
                    <v-avatar size="42" :color="announcementColor(item.a.kind)">
                      <v-icon dark>{{ announcementIcon(item.a.kind) }}</v-icon>
                    </v-avatar>
                    <div class="ml-3">
                      <div class="feed-card-title">{{ item.a.title }}</div>
                      <div class="caption grey--text">{{ item.date }} ・ {{ announcementKindLabel(item.a.kind) }}</div>
                    </div>
                  </div>
                  <v-card-text class="feed-card-body">
                    {{ item.a.body }}
                    <div v-if="item.a.url" class="mt-1">
                      <v-btn text small color="primary" @click="openAnnouncement(item.a)">
                        {{ $tt('feed.readMore') }}
                      </v-btn>
                    </div>
                  </v-card-text>
                </v-card>

                <!-- 作業活動エントリ (日次/週次) -->
                <v-card v-else :key="'e-' + item.date" class="feed-card mb-4">
                  <div class="feed-card-head">
                    <v-avatar size="42" color="green darken-1">
                      <v-icon dark>mdi-tractor-variant</v-icon>
                    </v-avatar>
                    <div class="ml-3">
                      <div class="feed-card-title">
                        {{
                          item.win === 'daily'
                            ? $tt('feed.entryDaily', { date: item.date })
                            : $tt('feed.entryWeekly', { date: item.date })
                        }}
                      </div>
                      <div class="caption grey--text">{{ $tt('feed.' + item.win) }}</div>
                    </div>
                  </div>
                  <v-card-text>
                    <div
                      v-for="row in entryRows(item)"
                      :key="item.date + '-' + row.scope"
                      class="feed-stat-row feed-stat-row--clickable"
                      @click="openHistory(row.scope, item.win)"
                    >
                      <div class="feed-stat-win">
                        <v-icon small class="mr-1">{{ row.icon }}</v-icon>
                        {{ row.label }}
                      </div>
                      <div class="feed-stat">
                        <div class="feed-stat-label">{{ $tt('feed.vsNormal') }}</div>
                        <div class="feed-stat-value" :class="ratioClass(row.normal_ratio)">
                          {{ ratioLabel(row.normal_ratio) }}
                        </div>
                      </div>
                      <div class="feed-stat">
                        <div class="feed-stat-label">{{ $tt('feed.vsLastYear') }}</div>
                        <div class="feed-stat-value" :class="ratioClass(row.yoy_ratio)">
                          {{ ratioLabel(row.yoy_ratio) }}
                        </div>
                      </div>
                      <v-icon small class="feed-stat-chart-icon">mdi-chart-line</v-icon>
                    </div>
                  </v-card-text>
                </v-card>
              </template>

              <p v-if="!timelineItems.length" class="grey--text">{{ $tt('feed.noData') }}</p>
            </template>
          </v-col>

          <!-- サイドカラム -->
          <v-col cols="12" md="4">
            <template v-if="!loading && !error">
              <!-- モニター週次サマリー -->
              <v-card class="feed-card mb-4">
                <div class="feed-card-head">
                  <v-avatar size="42" color="light-green darken-1">
                    <v-icon dark>mdi-sprout</v-icon>
                  </v-avatar>
                  <div class="ml-3">
                    <div class="feed-card-title">{{ $tt('feed.monitorTitle') }}</div>
                  </div>
                </div>
                <v-card-text>
                  <div v-for="s in signals" :key="s.commodity" class="mb-1">
                    <strong>{{ s.commodity === 'wheat' ? 'Wheat' : 'Soybean' }}</strong>
                    <span class="caption grey--text">({{ s.week_start }})</span><br />
                    {{ s.verdict && s.verdict !== 'none' ? $tt('feed.monitorActive') : $tt('feed.monitorNone') }}
                  </div>
                  <v-btn text small color="primary" to="/market-insights" class="pl-0">
                    {{ $tt('feed.toMarketInsights') }}
                  </v-btn>
                </v-card-text>
              </v-card>

              <!-- 受信フォローリクエスト -->
              <v-card v-if="incomingRequests.length" class="feed-card mb-4">
                <div class="feed-card-head">
                  <v-avatar size="42" color="blue-grey">
                    <v-icon dark>mdi-account-plus</v-icon>
                  </v-avatar>
                  <div class="ml-3">
                    <div class="feed-card-title">{{ $tt('customFollowRequest') }}</div>
                  </div>
                </div>
                <v-card-text>
                  <div v-for="r in incomingRequests" :key="r.username" class="d-flex align-center mb-2">
                    <span class="mr-2">{{ r.nickname || r.username }}</span>
                    <v-spacer />
                    <v-btn x-small color="primary" class="mr-1" @click="acceptFollow(r.username)">
                      {{ $tt('feed.followAccept') }}
                    </v-btn>
                    <v-btn x-small text @click="declineFollow(r.username)">{{ $tt('feed.followDecline') }}</v-btn>
                  </div>
                </v-card-text>
              </v-card>

              <p class="caption grey--text px-1">{{ $tt('feed.disclaimer') }}</p>
            </template>
          </v-col>
        </v-row>
      </v-col>
    </v-row>

    <!-- 投稿削除の確認 -->
    <v-dialog :value="!!deleteTarget" max-width="360" @input="deleteTarget = null">
      <v-card>
        <v-card-text class="pt-4">{{ $tt('feed.postDeleteConfirm') }}</v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="deleteTarget = null">{{ $tt('customCancel') }}</v-btn>
          <v-btn color="error" text @click="deletePost()">{{ $tt('feed.postDelete') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- 写真拡大 -->
    <v-dialog :value="!!photoView" max-width="900" @input="photoView = null">
      <v-card v-if="photoView" @click="photoView = null">
        <img :src="photoView.url" style="width: 100%; display: block" />
      </v-card>
    </v-dialog>

    <!-- 通報ダイアログ -->
    <v-dialog :value="!!reportTarget" max-width="420" @input="reportTarget = null">
      <v-card>
        <v-card-title class="subtitle-1">{{ $tt('feed.reportTitle') }}</v-card-title>
        <v-card-text>
          <p class="mb-2">{{ $tt('feed.reportConfirm') }}</p>
          <v-textarea v-model="reportReason" :label="$tt('feed.reportReason')" counter="200" rows="2" outlined dense />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="reportTarget = null">{{ $tt('customCancel') }}</v-btn>
          <v-btn color="warning" text :loading="reporting" @click="reportPost()">{{ $tt('feed.report') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- 過去動向グラフ (R17) -->
    <v-dialog v-model="historyDialog" max-width="720">
      <v-card>
        <v-card-title class="subtitle-1">
          {{ historyScopeLabel }} ・ {{ historyWin ? $tt('feed.' + historyWin) : '' }} ・
          {{ $tt('feed.historyTitle') }}
        </v-card-title>
        <v-card-text>
          <v-progress-linear v-if="historyLoading" indeterminate class="mb-2" />
          <v-alert v-if="historyError" type="warning" outlined dense>{{ $tt('feed.loadError') }}</v-alert>
          <e-chart
            v-if="!historyLoading && !historyError && historySeries.length"
            :options="historyChartOptions"
            :theme="echartsTheme"
            autoresize
            class="feed-history-chart"
          />
          <p v-if="!historyLoading && !historyError && !historySeries.length" class="grey--text mb-0">
            {{ $tt('feed.noData') }}
          </p>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- 投稿/イベントの公開設定の事後変更 -->
    <v-dialog :value="!!visEditTarget" max-width="420" @input="visEditTarget = null">
      <v-card>
        <v-card-title class="subtitle-1">{{ $tt('feed.visEditTitle') }}</v-card-title>
        <v-card-text v-if="visEditTarget">
          <v-select
            v-model="visEditVisibility"
            :items="eventVisibilityItems"
            dense
            :label="$tt('feed.eventSettingsVisibility')"
          />
          <v-checkbox
            v-if="visEditTarget.route && visEditTarget.route.length"
            v-model="visEditLocationExact"
            dense
            hide-details
            :disabled="visEditVisibility === 'public'"
            :label="$tt('feed.visEditLocationExact')"
          />
          <v-checkbox
            v-if="visEditTarget.location"
            v-model="visEditRemoveLocation"
            dense
            hide-details
            :label="$tt('feed.visEditRemoveLocation')"
          />
          <p v-if="visEditVisibility === 'public'" class="caption grey--text mt-2 mb-0">
            {{ $tt('feed.visEditPublicNote') }}
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text :disabled="visEditSaving" @click="visEditTarget = null">{{ $tt('customCancel') }}</v-btn>
          <v-btn color="primary" :loading="visEditSaving" @click="saveVisibilityEdit()">
            {{ $tt('customSaveAction') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- フィード設定 (イベント自動投稿・公開範囲・位置情報・SNS共有時の既定) -->
    <v-dialog v-model="settingsDialog" max-width="480">
      <v-card>
        <v-card-title class="subtitle-1">{{ $tt('feed.eventSettingsTitle') }}</v-card-title>
        <v-card-text v-if="feedSettings" style="max-height: 65vh; overflow-y: auto">
          <div class="caption grey--text mb-1">{{ $tt('feed.eventSettingsLocation') }}</div>
          <v-select
            v-model="feedSettings.location_visibility"
            :items="locationVisibilityItems"
            dense
            hide-details
            class="mb-3"
          />
          <v-checkbox
            v-model="feedSettings.sns_strip_location"
            dense
            hide-details
            class="mb-3"
            :label="$tt('feed.eventSettingsSnsStrip')"
          />
          <v-divider class="mb-2" />
          <!-- イベント種別ごとに「アップしない〜全体公開」を選ぶ (全体公開は絶対位置が自動で外れる) -->
          <div class="caption grey--text mb-1">{{ $tt('feed.eventSettingsPrefs') }}</div>
          <v-row v-for="t in eventTypeList" :key="t" dense align="center" class="my-0">
            <v-col cols="6" class="caption py-1">{{ eventTypeLabel(t) }}</v-col>
            <v-col cols="6" class="py-1">
              <v-select v-model="feedSettings.event_prefs[t]" :items="eventPrefItems" dense hide-details />
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text :disabled="settingsSaving" @click="settingsDialog = false">{{ $tt('customCancel') }}</v-btn>
          <v-btn color="primary" :loading="settingsSaving" @click="saveFeedSettings()">
            {{ $tt('customSaveAction') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import ECharts from 'vue-echarts'
import UserInfo from '@/lib/user-info'
import connectDatastoreClient from '@/lib/connect-client/connect-datastore-client'
import * as Constants from '@/lib/constants'
import feedApiClient from '@/lib/feed-api-client'
import { paletteRgb } from '@/lib/zone-palette'
import Toast from '@/lib/toast'

require('@/lib/echarts/themes/dark')

export default {
  name: 'Feed',
  components: { 'e-chart': ECharts },
  data() {
    return {
      loading: true,
      error: false,
      cell: null, // { la, lo }
      areaSource: 'none', // 'fields' | 'stored' | 'none'
      tier: 'free',
      entries: [],
      signals: [],
      announcements: [],
      posts: [],
      settingsDialog: false,
      settingsSaving: false,
      feedSettings: null,
      visEditTarget: null,
      visEditVisibility: 'private',
      visEditLocationExact: false,
      visEditRemoveLocation: false,
      visEditSaving: false,
      consented: false,
      deleteTarget: null,
      reportTarget: null,
      reportReason: '',
      reporting: false,
      followState: {},
      incomingRequests: [],
      photoView: null,
      historyDialog: false,
      historyLoading: false,
      historyError: false,
      historyScope: null,
      historyWin: null,
      historySeries: [],
    }
  },
  computed: {
    canWebShare() {
      return !!navigator.share
    },
    isPaidMember() {
      return !UserInfo.hasFreeFunctionality()
    },
    echartsTheme() {
      return this.$vuetify.theme.dark ? 'dark' : null
    },
    visibleAnnouncements() {
      const pick = l => this.announcements.filter(a => a.lang === l)
      const lang = this.$vuetify.lang.current || 'en'
      let list = pick(lang)
      if (!list.length && lang !== 'en') list = pick('en')
      if (!list.length && lang !== 'ja') list = pick('ja')
      return list.slice(0, 8)
    },
    timelineItems() {
      const items = []
      this.visibleAnnouncements.forEach(a => {
        items.push({ type: 'announcement', date: a.posted_date, a })
      })
      this.posts.forEach(p => {
        items.push({ type: 'post', date: p.posted_date, p })
      })
      this.entries.forEach(e => {
        items.push({ type: 'activity', date: e.date, win: e.win, areas: e.areas || {}, national: e.national })
      })
      // 日付降順。同日は お知らせ → ユーザー投稿 → 統計 の順
      const weight = { announcement: 0, post: 1, activity: 2 }
      return items.sort((x, y) =>
        x.date < y.date ? 1 : x.date > y.date ? -1 : (weight[x.type] || 0) - (weight[y.type] || 0)
      )
    },
    historyScopeLabel() {
      if (!this.historyScope) return ''
      return this.scopeLabel(this.historyScope)
    },
    eventTypeList() {
      return this.feedSettings ? Object.keys(this.feedSettings.event_prefs) : []
    },
    eventPrefItems() {
      return [{ text: this.$tt('feed.eventPrefOff'), value: 'off' }].concat(this.eventVisibilityItems)
    },
    eventVisibilityItems() {
      return [
        { text: this.$tt('feed.visPrivate'), value: 'private' },
        { text: this.$tt('feed.visFollowers'), value: 'followers' },
        { text: this.$tt('feed.visNeighborhood'), value: 'neighborhood' },
        { text: this.$tt('feed.visPublic'), value: 'public' },
      ]
    },
    locationVisibilityItems() {
      return [
        { text: this.$tt('feed.locHidden'), value: 'hidden' },
        { text: this.$tt('feed.locApprox'), value: 'approx' },
        { text: this.$tt('feed.locExact'), value: 'exact' },
      ]
    },
    historyChartOptions() {
      const pct = r => (r === null || r === undefined ? null : Math.round((r - 1) * 100))
      return {
        tooltip: { trigger: 'axis' },
        legend: { data: [this.$tt('feed.vsNormal'), this.$tt('feed.vsLastYear')] },
        grid: { left: 48, right: 16, top: 40, bottom: 28 },
        xAxis: { type: 'category', data: this.historySeries.map(p => p.date) },
        yAxis: { type: 'value', axisLabel: { formatter: '{value}%' } },
        series: [
          {
            name: this.$tt('feed.vsNormal'),
            type: 'line',
            data: this.historySeries.map(p => pct(p.normal_ratio)),
            showSymbol: false,
            smooth: true,
          },
          {
            name: this.$tt('feed.vsLastYear'),
            type: 'line',
            data: this.historySeries.map(p => pct(p.yoy_ratio)),
            showSymbol: false,
            smooth: true,
          },
        ],
      }
    },
  },
  created() {
    this.load()
  },
  methods: {
    scopeLabel(scope) {
      if (scope === 'c3') return this.$tt('feed.areaC3')
      if (scope === 'c5') return this.$tt('feed.areaC5')
      return this.$tt('feed.areaJp')
    },
    entryRows(item) {
      const rows = []
      if (item.areas.c3) {
        rows.push(Object.assign({ scope: 'c3', icon: 'mdi-map-marker', label: this.scopeLabel('c3') }, item.areas.c3))
      }
      if (item.areas.c5) {
        rows.push(Object.assign({ scope: 'c5', icon: 'mdi-map', label: this.scopeLabel('c5') }, item.areas.c5))
      }
      if (item.national) {
        rows.push(Object.assign({ scope: 'jp', icon: 'mdi-earth', label: this.scopeLabel('jp') }, item.national))
      }
      return rows
    },
    announcementIcon(kind) {
      if (kind === 'pr') return 'mdi-bullhorn-outline'
      if (kind === 'signal') return 'mdi-sprout'
      return 'mdi-newspaper-variant-outline'
    },
    announcementColor(kind) {
      if (kind === 'pr') return 'orange darken-1'
      if (kind === 'signal') return 'light-green darken-1'
      return 'primary'
    },
    announcementKindLabel(kind) {
      if (kind === 'pr') return this.$tt('feed.kindPr')
      if (kind === 'signal') return this.$tt('feed.kindSignal')
      return this.$tt('feed.kindNews')
    },
    countryFlag(code) {
      if (!code || code.length !== 2) return ''
      return code
        .toUpperCase()
        .split('')
        .map(c => String.fromCodePoint(127397 + c.charCodeAt(0)))
        .join('')
    },
    postStats(p) {
      const stats = []
      if (p.duration_min != null) {
        const m = p.duration_min
        stats.push({
          label: this.$tt('feed.statTime'),
          value: m >= 60 ? Math.floor(m / 60) + ' h ' + (m % 60) + ' min' : m + ' min',
        })
      }
      if (p.distance_m != null) {
        stats.push({
          label: this.$tt('feed.statDistance'),
          value: p.distance_m >= 1000 ? (p.distance_m / 1000).toFixed(1) + ' km' : Math.round(p.distance_m) + ' m',
        })
      }
      if (p.area_m2 != null) {
        stats.push({ label: this.$tt('feed.statArea'), value: (p.area_m2 / 10000).toFixed(2) + ' ha' })
      }
      if (p.width_m != null) {
        stats.push({ label: this.$tt('feed.statWidth'), value: p.width_m.toFixed(1) + ' m' })
      }
      if (p.speed_ms != null) {
        stats.push({ label: this.$tt('feed.statSpeed'), value: (p.speed_ms * 3.6).toFixed(1) + ' km/h' })
      }
      if (p.gnss) {
        stats.push({ label: this.$tt('feed.statGnss'), value: p.gnss })
      }
      return stats
    },
    routeMapUrl(route) {
      // 正確位置の公開を明示選択した投稿のみ。URL 長対策で最大100点に間引く
      const step = Math.max(1, Math.ceil(route.length / 100))
      const pts = route.filter((_, i) => i % step === 0)
      const path = 'color:0x43A047FF|weight:3|' + pts.map(p => p[0] + ',' + p[1]).join('|')
      return (
        'https://maps.googleapis.com/maps/api/staticmap?size=640x280&maptype=roadmap&path=' +
        encodeURIComponent(path) +
        '&key=' +
        Constants.GOOGLE_MAP_API_KEY
      )
    },
    routePoints(route) {
      // [[lat, lon], ...] を 100x56 ビューボックスへ正規化 (緯度で経度スケール補正)
      const lats = route.map(p => p[0])
      const lons = route.map(p => p[1])
      const latMin = Math.min(...lats)
      const latMax = Math.max(...lats)
      const lonMin = Math.min(...lons)
      const lonMax = Math.max(...lons)
      const cosLat = Math.cos(((latMin + latMax) / 2) * (Math.PI / 180))
      const w = Math.max((lonMax - lonMin) * cosLat, 1e-9)
      const h = Math.max(latMax - latMin, 1e-9)
      const pad = 4
      const scale = Math.min((100 - pad * 2) / w, (56 - pad * 2) / h)
      const ox = (100 - w * scale) / 2
      const oy = (56 - h * scale) / 2
      return route
        .map(p => {
          const x = ox + (p[1] - lonMin) * cosLat * scale
          const y = 56 - (oy + (p[0] - latMin) * scale)
          return x.toFixed(1) + ',' + y.toFixed(1)
        })
        .join(' ')
    },
    // ---- フィード設定 (イベント自動投稿) ----
    async openFeedSettings() {
      this.settingsDialog = true
      try {
        const res = await feedApiClient.getFeedSettings()
        this.feedSettings = res.data
      } catch (e) {
        console.error(e.response || e)
      }
    },
    async saveFeedSettings() {
      this.settingsSaving = true
      try {
        await feedApiClient.putFeedSettings(this.feedSettings)
        this.settingsDialog = false
      } catch (e) {
        console.error(e.response || e)
        Toast.i18nError('feed.shareFailed')
      } finally {
        this.settingsSaving = false
      }
    },
    // 形状 (Polygon/MultiPolygon) の外環リスト
    // 投稿時刻 (端末ローカル時刻の HH:mm)
    postTime(p) {
      if (!p.created_at) return ''
      const d = new Date(p.created_at)
      if (isNaN(d.getTime())) return ''
      return (
        d
          .getHours()
          .toString()
          .padStart(2, '0') +
        ':' +
        d
          .getMinutes()
          .toString()
          .padStart(2, '0')
      )
    },
    hasEventGeo(p) {
      return !!(p.shape || p.location)
    },
    shapeRings(p) {
      const g = p.shape
      if (!g) return []
      if (g.type === 'zones') {
        const rings = []
        for (const z of g.zones || []) {
          rings.push(...this.geomOuterRings(z.g))
        }
        return rings
      }
      return this.geomOuterRings(g)
    },
    geomOuterRings(g) {
      if (!g || !g.coordinates) return []
      if (g.type === 'Polygon') return g.coordinates[0] && g.coordinates[0].length >= 3 ? [g.coordinates[0]] : []
      if (g.type === 'MultiPolygon') {
        return g.coordinates.map(poly => poly[0]).filter(r => r && r.length >= 3)
      }
      return []
    },
    // ゾーン形状のバウンズ→ビューボックス正規化関数 (圃場外形 o も含めて揃える)
    zoneToPathFn(g) {
      let minX = Infinity
      let maxX = -Infinity
      let minY = Infinity
      let maxY = -Infinity
      const rings = []
      for (const z of g.zones || []) rings.push(...this.geomOuterRings(z.g))
      if (g.o) rings.push(...this.geomOuterRings(g.o))
      for (const ring of rings) {
        for (const c of ring) {
          minX = Math.min(minX, c[0])
          maxX = Math.max(maxX, c[0])
          minY = Math.min(minY, c[1])
          maxY = Math.max(maxY, c[1])
        }
      }
      if (minX === Infinity) return null
      const w = Math.max(maxX - minX, 1e-9)
      const h = Math.max(maxY - minY, 1e-9)
      const scale = Math.min(92 / w, 48 / h)
      return ring =>
        'M' +
        ring
          .map(c => {
            const x = 50 + (c[0] - (minX + maxX) / 2) * scale
            const y = 28 - (c[1] - (minY + maxY) / 2) * scale
            return x.toFixed(1) + ' ' + y.toFixed(1)
          })
          .join(' L') +
        ' Z'
    },
    // ゾーン色分けの SVG パス群 (VRA=黄→緑 / 高低差=緑→赤 のランプ)
    zonePaths(p) {
      const g = p.shape
      if (!g || g.type !== 'zones' || !g.zones) return []
      const toPath = this.zoneToPathFn(g)
      if (!toPath) return []
      const maxZone = Math.max(g.maxZone || 3, 2)
      // 色系統: ペイロードの p を優先。ない旧投稿はイベント種別ごとの旧配色
      const palette = g.p || (p.event_type === 'elevation_map_added' ? 'gnrd' : 'ylgn')
      return [...g.zones]
        .sort((a, b) => (a.z || 0) - (b.z || 0))
        .map(z => ({
          d: this.geomOuterRings(z.g)
            .map(toPath)
            .join(' '),
          color: this.zoneRampColor((z.z - 1) / (maxZone - 1), palette),
        }))
        .filter(zp => zp.d)
    },
    // 圃場外形線 (ゾーン色分けの上に重ねる)
    zoneOutlinePath(p) {
      const g = p.shape
      if (!g || g.type !== 'zones' || !g.o) return ''
      const toPath = this.zoneToPathFn(g)
      if (!toPath) return ''
      return this.geomOuterRings(g.o)
        .map(toPath)
        .join(' ')
    },
    zoneRampColor(t, palette) {
      const c = paletteRgb(palette, t)
      return 'rgba(' + c[0] + ',' + c[1] + ',' + c[2] + ',0.85)'
    },
    // 全リングを W x H のビューボックスへ正規化して SVG パスにする
    shapePath(p, W, H, pad) {
      const rings = this.shapeRings(p)
      if (!rings.length) return ''
      let minX = Infinity
      let maxX = -Infinity
      let minY = Infinity
      let maxY = -Infinity
      for (const ring of rings) {
        for (const c of ring) {
          minX = Math.min(minX, c[0])
          maxX = Math.max(maxX, c[0])
          minY = Math.min(minY, c[1])
          maxY = Math.max(maxY, c[1])
        }
      }
      const w = Math.max(maxX - minX, 1e-9)
      const h = Math.max(maxY - minY, 1e-9)
      const scale = Math.min((W - pad * 2) / w, (H - pad * 2) / h)
      return rings
        .map(ring => {
          const pts = ring.map(c => {
            const x = W / 2 + (c[0] - (minX + maxX) / 2) * scale
            const y = H / 2 - (c[1] - (minY + maxY) / 2) * scale
            return x.toFixed(1) + ' ' + y.toFixed(1)
          })
          return 'M' + pts.join(' L') + ' Z'
        })
        .join(' ')
    },
    shapePathSmall(p) {
      return this.shapePath(p, 60, 40, 2)
    },
    shapePathLarge(p) {
      return this.shapePath(p, 100, 56, 4)
    },
    // 圃場・マップ形状の地図背景 (絶対位置ありのイベントのみ)。複数圃場は path を複数並べる
    shapeMapUrl(p) {
      const rings = this.shapeRings(p)
      if (!rings.length) return this.pointMapUrl(p.location)
      const paths = rings
        .slice(0, 8)
        .map(ring => {
          const step = Math.max(1, Math.ceil(ring.length / 40))
          const pts = ring.filter((_, i) => i % step === 0)
          return (
            'path=' +
            encodeURIComponent(
              'color:0x43A047FF|weight:2|fillcolor:0x43A04740|' + pts.map(c => c[1] + ',' + c[0]).join('|')
            )
          )
        })
        .join('&')
      return (
        'https://maps.googleapis.com/maps/api/staticmap?size=640x280&maptype=satellite&' +
        paths +
        '&key=' +
        Constants.GOOGLE_MAP_API_KEY
      )
    },
    // 地点 (POI・作業位置) のマーカー地図
    pointMapUrl(loc) {
      return (
        'https://maps.googleapis.com/maps/api/staticmap?size=640x280&maptype=roadmap&zoom=15&markers=' +
        encodeURIComponent('color:green|' + loc.lat + ',' + loc.lon) +
        '&key=' +
        Constants.GOOGLE_MAP_API_KEY
      )
    },
    openVisibilityEdit(p) {
      this.visEditTarget = p
      this.visEditVisibility = p.visibility
      this.visEditLocationExact = !!p.location_exact
      this.visEditRemoveLocation = false
    },
    async saveVisibilityEdit() {
      const p = this.visEditTarget
      if (!p) return
      this.visEditSaving = true
      try {
        await feedApiClient.updatePost(p.id, {
          visibility: this.visEditVisibility,
          location_exact: this.visEditLocationExact,
          remove_location: this.visEditRemoveLocation || undefined,
        })
        this.visEditTarget = null
        this.load()
      } catch (e) {
        console.error(e.response || e)
        if (e.response && e.response.status === 428) {
          Toast.i18nError('feed.consentRequiredToShare')
        } else {
          Toast.i18nError('feed.shareFailed')
        }
      } finally {
        this.visEditSaving = false
      }
    },
    eventIcon(t) {
      return (
        {
          work_start: 'mdi-play-circle-outline',
          work_end: 'mdi-stop-circle-outline',
          field_enter: 'mdi-location-enter',
          field_exit: 'mdi-location-exit',
          poi_added: 'mdi-map-marker-plus',
          vra_map_added: 'mdi-grid',
          elevation_map_added: 'mdi-terrain',
          refline_added: 'mdi-ray-start-end',
          field_added: 'mdi-shape-polygon-plus',
          field_set_added: 'mdi-select-group',
        }[t] || 'mdi-bell-outline'
      )
    },
    eventTypeLabel(t) {
      return this.$tt('feed.event_' + t)
    },
    eventLabel(p) {
      const label = this.$tt('feed.event_' + p.event_type + '_text', { name: p.title || '' })
      // 圃場名などが無いイベントは空括弧を落とす
      return p.title
        ? label
        : label.replace(/\s*[(（]\s*[」"]?\s*["「]?\s*[)）]/g, '').replace(/[「（(]\s*[」)）]/g, '')
    },
    visibilityLabel(v) {
      if (v === 'public') return this.$tt('feed.visPublic')
      if (v === 'neighborhood') return this.$tt('feed.visNeighborhood')
      if (v === 'private') return this.$tt('feed.visPrivate')
      return this.$tt('feed.visFollowers')
    },
    shareUrl(post) {
      return Constants.FEED_API_URL + '/share/' + post.id
    },
    shareText(post) {
      const parts = [post.operation_type, post.text].filter(Boolean)
      return (parts.join(' - ') || 'AgriBus') + '\n#agribus #agribusnavi'
    },
    shareToX(post) {
      const u =
        'https://twitter.com/intent/tweet?text=' +
        encodeURIComponent(this.shareText(post)) +
        '&url=' +
        encodeURIComponent(this.shareUrl(post))
      window.open(u, '_blank', 'noopener')
    },
    shareToFacebook(post) {
      const u =
        'https://www.facebook.com/sharer/sharer.php?u=' +
        encodeURIComponent(this.shareUrl(post)) +
        '&quote=' +
        encodeURIComponent(this.shareText(post))
      window.open(u, '_blank', 'noopener')
    },
    async shareToInstagram(post) {
      // Instagram は Web からの直接投稿 API がないため、カード画像を共有シート経由で渡す。
      // 共有シート非対応 (デスクトップ等) の場合は画像を保存してもらう
      try {
        const blob = await (await fetch(this.shareUrl(post) + '/card.png')).blob()
        const file = new File([blob], 'agribus_share.png', { type: 'image/png' })
        if (navigator.canShare && navigator.canShare({ files: [file] })) {
          await navigator.share({ files: [file], text: this.shareText(post) })
          return
        }
        const a = document.createElement('a')
        a.href = URL.createObjectURL(blob)
        a.download = 'agribus_share.png'
        a.click()
        Toast.i18nSuccess('feed.instagramSaved')
      } catch (e) {
        console.error(e)
      }
    },
    async copyShareLink(post) {
      try {
        await navigator.clipboard.writeText(this.shareUrl(post))
        Toast.i18nSuccess('feed.linkCopied')
      } catch (e) {
        console.error(e)
      }
    },
    async webShare(post) {
      try {
        await navigator.share({ title: 'AgriBus', text: this.shareText(post), url: this.shareUrl(post) })
      } catch (e) {
        // キャンセル時も reject されるため黙殺
      }
    },
    async requestFollow(post) {
      try {
        await feedApiClient.requestFollow(post.author_username, UserInfo.getNickname())
        this.$set(this.followState, post.author_username, 'pending')
        Toast.i18nSuccess('customSendFollowRequestMessage')
      } catch (e) {
        console.error(e.response || e)
        Toast.i18nError('feed.shareFailed')
      }
    },
    async loadFollows() {
      try {
        const res = await feedApiClient.follows()
        this.incomingRequests = res.data.incoming || []
      } catch (e) {
        console.error(e.response || e)
      }
    },
    async acceptFollow(username) {
      try {
        await feedApiClient.acceptFollow(username, UserInfo.getNickname())
        this.incomingRequests = this.incomingRequests.filter(r => r.username !== username)
        Toast.i18nSuccess('feed.followAccepted')
      } catch (e) {
        console.error(e.response || e)
        Toast.i18nError('feed.shareFailed')
      }
    },
    async declineFollow(username) {
      try {
        await feedApiClient.declineFollow(username)
        this.incomingRequests = this.incomingRequests.filter(r => r.username !== username)
      } catch (e) {
        console.error(e.response || e)
        Toast.i18nError('feed.shareFailed')
      }
    },
    async toggleKudos(post) {
      try {
        const res = await feedApiClient.toggleKudos(post.id)
        post.kudos_mine = res.data.kudos_mine
        post.kudos_count = res.data.kudos_count
      } catch (e) {
        console.error(e.response || e)
      }
    },
    async deletePost() {
      const target = this.deleteTarget
      if (!target) return
      try {
        await feedApiClient.deletePost(target.id)
        this.posts = this.posts.filter(p => p.id !== target.id)
        Toast.i18nSuccess('feed.postDeleted')
      } catch (e) {
        console.error(e.response || e)
        Toast.i18nError('feed.shareFailed')
      } finally {
        this.deleteTarget = null
      }
    },
    async reportPost() {
      const target = this.reportTarget
      if (!target) return
      this.reporting = true
      try {
        await feedApiClient.reportPost(target.id, this.reportReason.slice(0, 200))
        Toast.i18nSuccess('feed.reportDone')
        this.reportReason = ''
        this.reportTarget = null
      } catch (e) {
        console.error(e.response || e)
        Toast.i18nError('feed.shareFailed')
      } finally {
        this.reporting = false
      }
    },
    ratioLabel(ratio) {
      if (ratio === null || ratio === undefined) return '—'
      const pct = Math.round((ratio - 1) * 100)
      if (pct > 5) return '↑ +' + pct + '%'
      if (pct < -5) return '↓ ' + pct + '%'
      return '→ ' + (pct >= 0 ? '+' : '') + pct + '%'
    },
    ratioClass(ratio) {
      if (ratio === null || ratio === undefined) return 'feed-flat'
      const pct = Math.round((ratio - 1) * 100)
      if (pct > 5) return 'success--text'
      if (pct < -5) return 'error--text'
      return 'feed-flat'
    },
    openAnnouncement(a) {
      if (!a.url) return
      if (a.url.charAt(0) === '/') {
        this.$router.push(a.url)
      } else {
        window.open(a.url, '_blank', 'noopener')
      }
    },
    async openHistory(scope, win) {
      this.historyScope = scope
      this.historyWin = win
      this.historyDialog = true
      this.historyLoading = true
      this.historyError = false
      this.historySeries = []
      try {
        const params = { scope, win }
        if (scope !== 'jp' && this.cell) {
          params.la = this.cell.la
          params.lo = this.cell.lo
        }
        const res = await feedApiClient.history(params)
        this.historySeries = res.data.series || []
      } catch (e) {
        console.error(e.response || e)
        this.historyError = true
      } finally {
        this.historyLoading = false
      }
    },
    async resolveCell() {
      const stored = localStorage.getItem('feed.cell')
      if (stored) {
        try {
          this.cell = JSON.parse(stored)
          this.areaSource = 'stored'
          return
        } catch (e) {
          localStorage.removeItem('feed.cell')
        }
      }
      try {
        const res = await connectDatastoreClient.getCentroidOfUserFields()
        const c = res.data || {}
        let lat, lon
        if (Array.isArray(c.coordinates) && c.coordinates.length >= 2) {
          // GeoJSON Point。仕様上は [lon, lat] だが本 API は軸順が保証されないため値域で判定
          // (|値| > 90 は経度としてしか成立しない。日本では常に一意に決まる)
          const [c0, c1] = c.coordinates
          if (Math.abs(c0) <= 90 && Math.abs(c1) > 90) {
            lat = c0
            lon = c1
          } else {
            lon = c0
            lat = c1
          }
        } else {
          lat = c.latitude !== undefined ? c.latitude : c.lat
          lon = c.longitude !== undefined ? c.longitude : c.lng !== undefined ? c.lng : c.lon
        }
        if (typeof lat === 'number' && typeof lon === 'number') {
          this.cell = { la: Math.floor(lat * 10), lo: Math.floor(lon * 10) }
          this.areaSource = 'fields'
          localStorage.setItem('feed.cell', JSON.stringify(this.cell))
        }
      } catch (e) {
        // 圃場なし (404)・取得失敗は全国表示のみにフォールバック
        this.cell = null
      }
    },
    async load() {
      this.loading = true
      this.error = false
      try {
        await this.resolveCell()
        const params = this.cell ? { la: this.cell.la, lo: this.cell.lo } : {}
        const res = await feedApiClient.timeline(params)
        this.tier = res.data.tier
        this.entries = res.data.entries || []
        this.signals = res.data.monitor_summary || []
        this.announcements = res.data.announcements || []
        this.posts = res.data.posts || []
        this.consented = !!res.data.consented
        this.followState = res.data.follow_state || {}
        this.loadFollows()
      } catch (e) {
        console.error(e.response || e)
        this.error = true
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style scoped>
.feed-page {
  max-width: 1200px;
}
.feed-page-title {
  font-size: 1.5rem;
  font-weight: 700;
  line-height: 1.3;
  margin-bottom: 4px;
}
.feed-page-desc {
  font-size: 0.875rem;
  margin-bottom: 20px;
}
.feed-card {
  border-radius: 12px;
}
.feed-card-head {
  display: flex;
  align-items: center;
  padding: 16px 16px 0;
}
.feed-card-title {
  font-size: 1rem;
  font-weight: 600;
  line-height: 1.3;
}
.feed-card-body {
  white-space: pre-line;
}
.feed-stat-row {
  display: flex;
  align-items: center;
  padding: 10px 0;
}
.feed-stat-row--clickable {
  cursor: pointer;
}
.feed-stat-row--clickable:hover {
  background: rgba(128, 128, 128, 0.08);
}
.feed-stat-row + .feed-stat-row {
  border-top: 1px solid rgba(128, 128, 128, 0.18);
}
.feed-stat-win {
  flex: 0 0 32%;
  font-size: 0.875rem;
  font-weight: 600;
}
.feed-stat {
  flex: 1;
}
.feed-stat-label {
  font-size: 0.6875rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  opacity: 0.6;
}
.feed-stat-value {
  font-size: 1.25rem;
  font-weight: 700;
}
.feed-stat-chart-icon {
  opacity: 0.5;
}
.feed-route {
  /* Strava 風: 地図はカード端まで流し込み */
  margin-left: -16px;
  margin-right: -16px;
  background: rgba(128, 128, 128, 0.06);
}
.feed-route svg {
  display: block;
  width: 100%;
  height: 300px;
}
.feed-event-desc {
  /* feed-card-body の pre-line を打ち消し、名前と説明を1行に流す */
  white-space: normal;
}
.feed-route-map {
  display: block;
  width: 100%;
  border-radius: 0;
  max-height: 340px;
  object-fit: cover;
}
.feed-photos {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.feed-photo-thumb {
  width: calc(50% - 3px);
  max-width: 220px;
  aspect-ratio: 4 / 3;
  object-fit: cover;
  border-radius: 6px;
  cursor: pointer;
}
.feed-post-stats {
  /* Strava 風: 大きめの数字を横並び、区切り線つき */
  display: flex;
  flex-wrap: wrap;
  gap: 8px 0;
  margin-bottom: 4px;
}
.feed-post-stat {
  flex: 0 0 auto;
  min-width: 96px;
  padding-right: 16px;
}
.feed-post-stat + .feed-post-stat {
  border-left: 1px solid rgba(128, 128, 128, 0.25);
  padding-left: 16px;
}
.feed-post-stat-value {
  font-size: 1.35rem;
  font-weight: 600;
  line-height: 1.2;
}
.feed-activity-title {
  font-size: 1.15rem;
  font-weight: 700;
  line-height: 1.3;
  margin-bottom: 2px;
}
.feed-flat {
  opacity: 0.85;
}
.feed-history-chart {
  width: 100%;
  height: 360px;
}
.feed-event-shape {
  width: 60px;
  height: 40px;
  flex: 0 0 auto;
}
</style>
